import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    isEpisode,
    isLiveTv,
    isSeries,
    isVod,
    isSeason,
    isArchive,
    isRecording
    // itemHasStarted,
    // getMinutesBeforeStart
} from 'Utils';
import { View, Text, Linking, ViewStyle } from 'react-native';
import { t } from 'Locale';
import { getRecordingForItem, getTranslatedPackageName, RECORDING_STATUS } from 'Utils/itemUtils';
import { hasRelevantPackage, isUserFreemium } from 'Utils/userUtils';
import { useStore } from 'Store/Store';
import { toLocaleDateString } from 'Utils/dateUtils';
// eslint-disable-next-line import/no-cycle
import { PreConsentScene, PackageInfoScene } from 'Pages';
import { Device } from 'DeviceUtils';
import { PLATFORMS } from 'Options';
import { ConfirmationModal, Icon } from 'LocalGluons';
import { EntitlementsResponse, NativeEntitlementsResponse } from '@24i/st-api-connector';
import { use } from 'i18next';
import TrailerButton from '../TrailerButton/TrailerButton';
import RecordingButton from '../RecordingButton/RecordingButton';
// import RemindMeButton from '../RemindMeButton/RemindMeButton';
import ShowTimesButton from '../ShowTimesButton';
import SubscriptionUpgradeButton from '../SubscriptionUpgradeButton/SubscriptionUpgradeButton';
import SubscriptionUpgradeDialog from '../SubscriptionUpgradeButton/SubscriptionUpgradeDialog';
import BadgeButton from '../BadgeButton/BadgeButton';
import defaultStyles from '../../Details.jss';
import RestartButton from '../RestartButton';
import VoyoUpsell from '../../../VoyoUpsell';
import VoyoConsentDialog from '../../../../../localGluons/components/VoyoConsentDialog/VoyoConsentDialog';
import { SUBSCRPTION_TYPE } from '../../../PackageInfoScene/getVoyoPackageTexts';
import styles from './ButtonsContainer.jss';
import { EntitlementsModal } from 'LocalGluons/components/EntitlementsModal';

interface ButtonsContainerProps {
    navigation: any;
    item: any;
    onTrailerPress: () => void;
    setModalContent: (modal: any) => void;
    onLoading: () => void;
    updateItem: () => void;
    parentItem: any;
    isArchiveAvailable: boolean;
    isDownloadable: boolean;
    hasTrailer: boolean;
    style: ViewStyle;
    isUpsellSceneExternal: boolean;
    entitlements: EntitlementsResponse;
    nativeEntitlements: NativeEntitlementsResponse;
    playouts: any;
    isLowLatency: boolean;
}

// Statements are used for easier reading & understanding
// eslint-disable-next-line max-statements
const ButtonsContainer = ({
    navigation,
    item,
    parentItem = null,
    style = {},
    entitlements,
    nativeEntitlements,
    playouts,
    onLoading,
    updateItem,
    setModalContent,
    onTrailerPress = () => { },
    hasTrailer = false,
    isUpsellSceneExternal = false,
    isLowLatency,
    // Do not remove, temporarily hidden
    // eslint-disable-next-line no-unused-vars
    isDownloadable = false,
    isArchiveAvailable = false
}: ButtonsContainerProps) => {
    const {
        userData,
        deviceStorageState: { language },
        mCoreConfig
    } = useStore();
    const { data: recData } = getRecordingForItem(item);
    const hasBeenAlreadyRecorded = Boolean(recData);
    const recStatus = hasBeenAlreadyRecorded && recData.status;
    const recValidTo = hasBeenAlreadyRecorded && recData.validTo;
    const dateString = toLocaleDateString(recValidTo, language);

    const validDate = recValidTo ? dateString : null;
    const { appManager } = useStore();
    const {
        backstageConnector: { auth }
    } = appManager.getApi();

    const platform = item?.data?.pckg?.platform;
    const isVoyo = item?.data?.pckg?.platform === PLATFORMS.VOYO;
    const isThirdPartyPlatform = Object.values(PLATFORMS).includes(platform);

    const [consentsDefinition, setConsentsDefinition] = useState('');
    const [currentPlayouts, setCurrentPlayouts] = useState(playouts);
    const hasConsent = useRef(Boolean(playouts?.consent));
    const userSubscription = playouts?.type;

    const recordingButtonProps = {
        navigation,
        item,
        parentItem,
        onLoading,
        updateItem
    };
    const userIsLoggedIn = Boolean(userData);

    const [packageName, setPackageName] = useState(getTranslatedPackageName(item, language));
    useEffect(() => {
        setPackageName(getTranslatedPackageName(item, language));
    }, [item, language]);

    const isItemCurrentlyAired = isLiveTv(item) && !isArchive(item);
    const showTrailer = isVod(item) && hasTrailer;
    const itemIsArchivedButNotRecording = isArchive(item) && !isRecording(item);
    const isItemRecording = isRecording(item) && userIsLoggedIn;
    const isTvShowWithTrailer = (isSeries(item) || isSeason(item) || isEpisode(item)) && hasTrailer;
    const recordingDoneAndValid = recStatus === RECORDING_STATUS.DONE && recValidTo;
    const hasBlackout = item?.hasBlackout && item?.hasBlackout();
    const showSubscriptionLine =
        isItemCurrentlyAired ||
        showTrailer ||
        itemIsArchivedButNotRecording ||
        isItemRecording ||
        isTvShowWithTrailer ||
        recordingDoneAndValid;
    const canUserRecord =
        userIsLoggedIn &&
        ((typeof item.isRecordable === 'function' && item.isRecordable()) ||
            hasBeenAlreadyRecorded);
    const isFreemium = isUserFreemium(userData);

    const isItemBeingAired =
        item?.data?.start < new Date().getTime() && new Date().getTime() < item?.data?.end;

    const closeModalCallback = () => {
        setModalContent(null);
    };


    const renderVoyoConsent = (definition) => () =>
        definition
            ? setModalContent(
                <VoyoConsentDialog
                    backgroundImageUrl={
                        Device.isTabletOrWeb
                            ? definition.backgroundImageUrlLandscape
                            : definition.backgroundImageUrlPortrait
                    }
                    isLandscape={Device.isTabletOrWeb}
                    close={closeModalCallback}
                    titleDescription={definition.titleDescription}
                    bottomDescription={definition.bottomDescription}
                    continueButtonText={definition.continueButtonText}
                    title={definition.title}
                    logoUrl={definition.logoUrl}
                    consentDefinitions={definition.consentDefinitions}
                    itemId={item.data.id}
                    setModalContent={setModalContent}
                />
            )
            : null;

    const openInfoPackage = () => {
        let otherProps = {};
        switch (platform) {
            case PLATFORMS.CANALPLUS:
                if (Device.isWeb) {
                    navigation.navigate('CanalplusAboutService', {
                        close: () => {
                            navigation.pop();
                        }
                    });
                    return;
                }
                otherProps = {
                    backgroundImage: Device.isTablet
                        ? t('PARTNER.CANALPLUS.ABOUT.IMG_URL_TABLET_WEB')
                        : t('PARTNER.CANALPLUS.ABOUT.IMG_URL_MOBILES'),
                    payload: {
                        continueButtonText: 'PARTNER.CANALPLUS.I_UNDERSTAND_BTN',
                        title: '',
                        description: '',
                        backButtonText: '',
                        infoDescription: ''
                    },
                    hr: false,
                    close: closeModalCallback,
                    ...styles.CanalPlus
                };
                break;
            case PLATFORMS.VOYO:
                otherProps = {
                    onContinue: () => setModalContent(<VoyoUpsell close={closeModalCallback} />),
                    close: closeModalCallback
                };
                break;
            default:
        }
        setModalContent(<PackageInfoScene platform={platform} {...otherProps} />);
    };
    const onBadgeButtonPressed = async () => {
        if (!hasConsent.current) {
            const {
                backstageConnector: { vod }
            } = appManager.getApi();
            try {
                if (platform === PLATFORMS.VOYO) {
                    const playoutsUpdate = await vod.getPlayouts(platform);
                    setCurrentPlayouts(playoutsUpdate);
                    hasConsent.current = playoutsUpdate.consent;
                } else {
                    const consents = await auth.getPartnerConsentsDefinition(platform);
                    hasConsent.current = consents;
                }
            } catch {
                console.log(Error);
            }
        }
        if (hasConsent.current) {
            openInfoPackage();
        } else {
            let consents = consentsDefinition;
            if (!consents) {
                try {
                    consents = await auth.getConsentsDefinition(platform);
                    setConsentsDefinition(consents);
                } catch {
                    console.log(Error);
                }
            }
            if (userSubscription === SUBSCRPTION_TYPE.TRIAL) {
                setModalContent(
                    <PreConsentScene
                        close={closeModalCallback}
                        onContinue={renderVoyoConsent(consents)}
                    />
                );
            } else {
                renderVoyoConsent(consents)();
            }
        }
    };

    const handleDeepLink = () => {
        const deepLink = mCoreConfig?.orderingServiceUrl;
        if (typeof deepLink === 'string' && deepLink.trim().length > 0) {
            Linking.openURL(deepLink.trim());
        }
    };

    return hasBlackout ? (
        <View style={[defaultStyles.infoWarningTextContainer]}>
            <Text style={[defaultStyles.infoWarningText]}>{`${t(
                'blackout_for_selected_schedule'
            )}`}</Text>
        </View>
    ) : (
        <View style={[defaultStyles.buttonsContainer, style]}>
            {!isVoyo &&
                (isVod(item) || isEpisode(item)) &&
                nativeEntitlements?.action === 'UPSELL_MESSAGE' && (
                    <SubscriptionUpgradeButton
                        isThirdPartyPlatform
                        onPress={() =>
                            setModalContent(
                                <EntitlementsModal
                                    logo={''}
                                    header={t(nativeEntitlements?.packageName)}
                                    message={t(nativeEntitlements?.templateTextString)}
                                    onCancel={closeModalCallback}
                                    onClose={closeModalCallback}
                                    confirmLabel={t('COMMON.FIND_MORE_INFO')}
                                    webString={t('SS.VOD.ORDER_SUBS_INFO_WEB')}
                                    storeString={t('SS.VOD.ORDER_SUBS_INFO_STORE')}
                                    mobileString={t('SS.VOD.ORDER_SUBS_INFO_CALL')}
                                    onConfirm={handleDeepLink}
                                />
                            )
                        }
                        packageName={packageName}
                        showLine={showSubscriptionLine}
                        nativeEntitlements={nativeEntitlements}
                    />
                )}
            {!isVoyo &&
                isVod(item) &&
                !hasRelevantPackage(item, userData) &&
                !nativeEntitlements && (
                    <SubscriptionUpgradeButton
                        isThirdPartyPlatform={isVoyo}
                        onPress={() =>
                            setModalContent(
                                <SubscriptionUpgradeDialog
                                    packageName={packageName}
                                    close={closeModalCallback}
                                    isFreemium={isUserFreemium(userData)}
                                    mCoreConfig={mCoreConfig}
                                    handleDeepLink={handleDeepLink}
                                />
                            )
                        }
                        packageName={packageName}
                        showLine={showSubscriptionLine}
                    />
                )}
            {isVoyo && isVod(item) && isUpsellSceneExternal && (
                <SubscriptionUpgradeButton
                    isThirdPartyPlatform={isVoyo}
                    onPress={() => setModalContent(<VoyoUpsell close={closeModalCallback} />)}
                    packageName={packageName}
                    showLine={showSubscriptionLine}
                    entitlements={entitlements}
                />
            )}
            {isItemCurrentlyAired && (
                <>
                    {/* Is user logged in, is item available for record or there is already some recording */}
                    {canUserRecord && !isFreemium && <RecordingButton {...recordingButtonProps} />}
                    {!isLowLatency && isItemBeingAired && <RestartButton item={item} />}
                    {/* <ShowTimesButton navigation={navigation} item={item} /> */}
                    {item.data.channelType !== 'VOD' && (
                        <ShowTimesButton navigation={navigation} item={item} />
                    )}
                    {/* {falseuserIsLoggedIn && !itemHasStarted(item) && (
                        <RemindMeButton
                            item={item}
                            minutesBeforeStart={getMinutesBeforeStart(item)}
                        />
                    )} */}
                </>
            )}
            {showTrailer && <TrailerButton testID="trailer_button" onPress={onTrailerPress} />}

            {isThirdPartyPlatform && (
                <BadgeButton
                    onPress={onBadgeButtonPressed}
                    platform={platform}
                    playouts={currentPlayouts}
                />
            )}
            {itemIsArchivedButNotRecording && !hasBlackout && (
                <>
                    {/* Is user logged in, is item available for record or there is already some recording */}
                    {canUserRecord && !isFreemium && <RecordingButton {...recordingButtonProps} />}
                    <ShowTimesButton navigation={navigation} item={item} />
                    {/* TODO: TEMPORARILY HIDE REMINDER AND DOWNLOAD BUTTON */}
                    {/* { */}
                    {/*    (userIsLoggedIn && isDownloadable) && ( */}
                    {/*        <DownloadButton item={item} /> */}
                    {/*    ) */}
                    {/* } */}
                </>
            )}
            {isItemRecording && !isFreemium && (
                <>
                    <RecordingButton {...recordingButtonProps} />
                    {/* TODO: TEMPORARILY HIDE REMINDER AND DOWNLOAD BUTTON */}
                    {/* {isDownloadable && (<DownloadButton item={item} />)} */}
                </>
            )}
            {isTvShowWithTrailer && (
                <TrailerButton testID="trailer_button" onPress={onTrailerPress} />
            )}
            {recordingDoneAndValid && (
                <Text style={defaultStyles.infoText}>{`${t(
                    'RECORDINGS.DETAIL_AVAILABLE'
                )} ${validDate}`}</Text>
            )}
        </View>
    );
};

export default ButtonsContainer;
